<template>
  <div v-if="couponId" class="modal-overlay">
    <div class="detail-container" @click.stop>
      <div class="modal-header">
        <h1 class="detail-title">Coupon Detail</h1>
        <button type="button" class="close-btn" aria-label="Close" @click="handleClose">
          <img :src="closeFillIcon" alt="" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <LoadingIndicator v-if="loading" min-height="320px" />
        <div v-else-if="error" class="detail-state detail-state--error">{{ error }}</div>
        <template v-else-if="detail">
        <div class="info-row">
          <span class="info-label">Coupon ID:</span>
          <span class="info-value">{{ detail.couponId }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">Name:</span>
          <span class="info-value">{{ detail.name }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">Coupon Code:</span>
          <span class="info-value">{{ detail.code }}</span>
        </div>

        <div class="info-row info-row--picture">
          <span class="info-label">Coupon Picture:</span>
          <img
            v-if="getCouponPictureUrl(detail.pictureUrl)"
            :src="getCouponPictureUrl(detail.pictureUrl)"
            alt="coupon"
            class="coupon-picture"
          />
          <span v-else class="info-value info-value--muted">No picture</span>
        </div>

        <div class="info-row">
          <span class="info-label">Discount Type:</span>
          <span class="info-value">{{ detail.discountType }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">Discount Amount:</span>
          <span class="info-value">{{ detail.discountAmount }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">Minimum Spend Requirement (Before Tax):</span>
          <span class="info-value">{{ detail.minSpend }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">Validity Period:</span>
          <span class="info-value">{{ detail.validityPeriod }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">Eligible Services:</span>
          <span class="info-value">{{ detail.eligibleServices }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">Usage Limit:</span>
          <span class="info-value">{{ detail.usageLimit }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">Eligible Regions:</span>
          <span class="info-value">{{ detail.eligibleRegions }}</span>
        </div>

        <div class="info-row">
          <span class="info-label">Total Redemptions:</span>
          <span class="info-value">{{ detail.totalRedemptions }}</span>
        </div>

        <div class="section-divider" />

        <div class="info-row">
          <span class="info-label">Visibility:</span>
          <span
            class="status-label"
            :class="{
              'status-label--visible': visibilityTone === 'visible',
              'status-label--hidden': visibilityTone === 'hidden',
            }"
          >
            {{ visibilityText }}
          </span>
        </div>

        <div class="info-row">
          <span class="info-label">Availability:</span>
          <span
            class="status-label"
            :class="{
              'status-label--active': availabilityTone === 'active',
              'status-label--inactive': availabilityTone === 'inactive',
            }"
          >
            {{ availabilityText }}
          </span>
        </div>

        <div class="info-row info-row--description info-row--with-divider">
          <span class="info-label">Description:</span>
          <span class="info-value">{{ detail.description }}</span>
        </div>
        </template>
      </div>

      <div v-if="detail" class="detail-footer">
        <div class="section-divider" />
        <div class="detail-actions">
          <button type="button" class="action-btn" @click="handleSetVisibility">
            <span class="action-icon-wrap" aria-hidden="true">
              <img :src="showIcon" alt="" class="action-icon-img action-icon-default" />
              <img :src="showIcon" alt="" class="action-icon-img action-icon-hover action-icon-hover--white" />
            </span>
            Set Visibility
          </button>
          <button
            v-if="isCouponActive"
            type="button"
            class="action-btn"
            @click="openStatusConfirm('deactivate')"
          >
            <span class="action-icon-wrap" aria-hidden="true">
              <img :src="inactivateIcon" alt="" class="action-icon-img action-icon-default" />
              <img :src="inactivateWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
            </span>
            Inactivate
          </button>
          <button
            v-else
            type="button"
            class="action-btn action-btn--reactivate"
            @click="openStatusConfirm('reactivate')"
          >
            <span class="action-icon-wrap" aria-hidden="true">
              <img :src="activateIcon" alt="" class="action-icon-img action-icon-default" />
              <img :src="activateWhiteIcon" alt="" class="action-icon-img action-icon-hover" />
            </span>
            Reactivate
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import { isCouponApiSuccess } from '@/api/couponApi'
import {
  mapCouponDetailToView,
  postCouponDetail,
  resolveCouponPictureUrl,
  type CouponDetailView,
} from '@/api/CouponDetai'
import closeFillIcon from '@/assets/Fill 1@2x.png'
import showIcon from '@/assets/show@2x.png'
import inactivateIcon from '@/assets/Schedule.png'
import inactivateWhiteIcon from '@/assets/deactivate_white@2x (1).png'
import activateIcon from '@/assets/activate.png'
import activateWhiteIcon from '@/assets/activate_white@2x.png'

const props = withDefaults(
  defineProps<{
    couponId: string
    recordId?: number
    isVisible?: boolean
    isActive?: boolean
  }>(),
  {
    recordId: 0,
    isVisible: true,
    isActive: true,
  },
)

export interface CouponDetailUpdatePayload {
  availability: 'active' | 'inactive'
}

export interface CouponDetailSetVisibilityPayload {
  recordId: number
  visibility: 'visible' | 'hidden'
}

export interface CouponDetailRequestStatusPayload {
  action: 'deactivate' | 'reactivate'
  recordId: number
  couponName: string
}

const emit = defineEmits<{
  close: []
  'set-visibility': [payload: CouponDetailSetVisibilityPayload]
  'request-status': [payload: CouponDetailRequestStatusPayload]
  updated: [payload: CouponDetailUpdatePayload]
}>()

const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => undefined,
)

const detail = ref<CouponDetailView | null>(null)
const detailRecordId = ref(0)
const loading = ref(false)
const error = ref('')

const visibilityText = computed(
  () => detail.value?.visibility ?? (props.isVisible ? 'Visible' : 'Hidden'),
)
const visibilityTone = computed(
  () => detail.value?.visibilityTone ?? (props.isVisible ? 'visible' : 'hidden'),
)
const availabilityText = computed(
  () => detail.value?.availability ?? (props.isActive ? 'Active' : 'Inactive'),
)
const availabilityTone = computed(
  () => detail.value?.availabilityTone ?? (props.isActive ? 'active' : 'inactive'),
)
const isCouponActive = computed(() => availabilityTone.value === 'active')
const statusRecordId = computed(() => detailRecordId.value || props.recordId || 0)

/** 与技师 DetailTechnicians.getAvatarUrl 相同：已是完整 URL 则直用，否则走 resolve */
function getCouponPictureUrl(picture: string): string {
  if (!picture || picture.trim() === '') return ''
  if (picture.startsWith('http') || picture.startsWith('blob:') || picture.startsWith('data:')) {
    return picture
  }
  return resolveCouponPictureUrl(picture)
}

async function fetchDetail(couponId: string): Promise<void> {
  const id = couponId.trim()
  if (!id) {
    detail.value = null
    error.value = ''
    return
  }

  loading.value = true
  error.value = ''
  detail.value = null

  try {
    const response = await postCouponDetail({ coupon_id: id })
    if (isCouponApiSuccess(response.code) && response.data?.coupon_id) {
      detailRecordId.value = Number(response.data.id ?? props.recordId ?? 0) || 0
      // pictureUrl 存原始 path（与技师 avatar 字段一致），展示时再拼 baseURL
      const mapped = mapCouponDetailToView(response.data, '')
      const raw =
        (typeof response.data.picture_url === 'string' && response.data.picture_url.trim()) ||
        (typeof response.data.picture === 'string' && response.data.picture.trim()) ||
        ''
      detail.value = {
        ...mapped,
        pictureUrl: raw.replace(/\\/g, '/') || mapped.pictureUrl,
      }
      return
    }
    error.value = response.msg || 'Failed to load coupon detail'
  } catch (fetchError) {
    console.error('Fetch coupon detail error:', fetchError)
    error.value = 'Failed to load coupon detail'
  } finally {
    loading.value = false
  }
}

watch(
  () => props.couponId,
  (couponId) => {
    void fetchDetail(couponId)
  },
  { immediate: true },
)

function handleClose(): void {
  emit('close')
}

function handleSetVisibility(): void {
  const recordId = statusRecordId.value
  if (!recordId) {
    showToast('error', 'Coupon record ID is missing')
    return
  }
  emit('set-visibility', {
    recordId,
    visibility: visibilityTone.value === 'visible' ? 'visible' : 'hidden',
  })
}

function openStatusConfirm(action: 'deactivate' | 'reactivate'): void {
  const recordId = statusRecordId.value
  if (!recordId) {
    showToast('error', 'Coupon record ID is missing')
    return
  }
  emit('request-status', {
    action,
    recordId,
    couponName: detail.value?.name?.trim() || props.couponId,
  })
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 24px 24px 45px 24px;
  z-index: 10000;
  box-sizing: border-box;
}

.detail-container {
  --cd-colon-x: 137px;
  --cd-label-width: 130px;
  --cd-pad-left: calc(var(--cd-colon-x) - var(--cd-label-width));
  background-color: #ffffff;
  border-radius: 12px;
  width: 540px;
  max-width: calc(100% - 48px);
  height: calc(100vh - 69px);
  max-height: calc(100vh - 69px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
}

.modal-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0 24px;
  flex-shrink: 0;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  font-family:
    'SF Pro',
    SFPro,
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 24px 0 16px;
  text-align: left;
  line-height: 20px;
}

.close-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  margin-top: 24px;
}

.modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 24px 0 var(--cd-pad-left);
  display: flex;
  flex-direction: column;
  gap: 16px;
  scrollbar-gutter: stable;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

.detail-state {
  padding: 24px 0;
  font-size: 14px;
  line-height: 20px;
  color: #666666;
}

.detail-state--error {
  color: #e54848;
}

.info-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex-shrink: 0;
  width: 100%;
}

.info-row--picture {
  align-items: flex-start;
}

.info-row--description {
  align-items: flex-start;
}

.info-row--with-divider {
  position: relative;
  padding-top: 16px;
  align-items: flex-start;
}

.info-row--with-divider::before {
  content: '';
  position: absolute;
  top: 0;
  left: calc(24px - var(--cd-pad-left));
  width: calc(100% - (24px - var(--cd-pad-left)));
  border-top: 1px solid #e0e0e0;
}

.info-label {
  box-sizing: border-box;
  flex: 0 0 var(--cd-label-width);
  width: var(--cd-label-width);
  max-width: var(--cd-label-width);
  text-align: right;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: normal;
  font-family: inherit;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
}

.info-value {
  flex: 1 1 auto;
  min-width: 0;
  font-family: inherit;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: normal;
}

.info-value--muted {
  color: #999999;
}

.status-label {
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  background: none;
  white-space: nowrap;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
}

.status-label--visible {
  color: #00aeef;
}

.status-label--hidden {
  color: #cccccc;
}

.status-label--active {
  color: #98d5be;
}

.status-label--inactive {
  color: #e54848;
}

.coupon-picture {
  width: 200px;
  height: auto;
  max-height: 120px;
  object-fit: contain;
  display: block;
}

.section-divider {
  height: 0;
  padding: 0;
  border: none;
  border-top: 1px solid #e0e0e0;
  flex-shrink: 0;
  align-self: flex-start;
  box-sizing: border-box;
}

.modal-body .section-divider {
  margin-left: calc(24px - var(--cd-pad-left));
  width: calc(100% - (24px - var(--cd-pad-left)));
}

.detail-footer {
  flex-shrink: 0;
  padding: 16px 24px 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: #ffffff;
}

.detail-footer .section-divider {
  width: 100%;
  margin-left: 0;
}

.detail-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 0 0 16px;
  background-color: #ffffff;
  flex-shrink: 0;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 16px;
  height: 28px;
  border: 1px solid #000000;
  background: #fff;
  border-radius: 16px;
  cursor: pointer;
  font-family: inherit;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000000;
  transition:
    background-color 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;
}

.action-btn:hover {
  background: #000000;
  border-color: #000000;
  color: #ffffff;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.action-btn:disabled:hover {
  background: #fff;
  border-color: #000000;
  color: #000000;
}

.action-btn--reactivate {
  height: 28px;
  border-radius: 16px;
}

.action-icon-wrap {
  position: relative;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.action-icon-img {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.action-icon-wrap .action-icon-default,
.action-icon-wrap .action-icon-hover {
  position: absolute;
  top: 0;
  left: 0;
  transition: opacity 0.2s ease;
}

.action-icon-wrap .action-icon-hover {
  opacity: 0;
}

.action-icon-wrap .action-icon-hover--white {
  filter: brightness(0) invert(1);
}

.action-btn:hover .action-icon-wrap .action-icon-default {
  opacity: 0;
}

.action-btn:hover .action-icon-wrap .action-icon-hover {
  opacity: 1;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .info-label {
    flex: none;
    width: auto;
    text-align: left;
  }
}
</style>
